import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  TableSortLabel,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { wheelService } from '../../services/wheelService';
import { useMarketPolling } from '../../hooks/useMarketPolling';
import { DiscoveryOptionRow, WheelDiscoveryDetail, WheelPosition, WheelStrategy } from '../../types/wheel';
import { fmt } from '../../utils/formatters';
import ExecuteLogPopup, { TradeDetails } from '../shared/ExecuteLogPopup';
import ClosePositionPopup from '../shared/ClosePositionPopup';
import PositionsList from '../shared/PositionsList';
import { WHEEL_REASONS } from '../shared/CloseTradeDialog';
import { userService } from '../../services/userService';

type OpMode = 'hide' | 'recommended' | 'all';

const WheelDetail: React.FC = () => {
  const { id, ticker: paramTicker } = useParams<{ id?: string; ticker?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const preferredExpiry = (location.state as any)?.expiry as string | undefined;

  // Discovery mode (from /discover/wheel/:ticker) vs strategy mode (from /wheel/:id)
  const isDiscovery = !id;
  const ticker = paramTicker?.toUpperCase();

  const [strategy, setStrategy] = useState<WheelStrategy | null>(null);
  const [discovery, setDiscovery] = useState<WheelDiscoveryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [opMode, setOpMode] = useState<OpMode>('recommended');
  const opModeRef = useRef<OpMode>('recommended');
  const opModeInitializedRef = useRef(false);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);

  type SortCol = 'expiry' | 'strike' | 'delta' | 'bid' | 'ask' | 'mid' | 'iv' | 'dte' | 'volume' | 'open_interest' | 'annualized_roi';
  const [sortCol, setSortCol] = useState<SortCol>('expiry');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [detailExpiry, setDetailExpiry] = useState<string>(preferredExpiry || '');
  const [availableExpiries, setAvailableExpiries] = useState<Array<{ date: string; label: string }>>([]);
  const [customDateMode, setCustomDateMode] = useState(false);

  const handleSort = (col: SortCol) => {
    if (col === sortCol) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const sortedPuts = (puts: DiscoveryOptionRow[]) =>
    [...puts].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const aVal = a[sortCol];
      const bVal = b[sortCol];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      return (Number(aVal) - Number(bVal)) * dir;
    });

  // Confirm dialogs
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [closeWheelConfirmOpen, setCloseWheelConfirmOpen] = useState(false);

  // Popups
  const [executeOpen, setExecuteOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<{ strike: number; expiry: string; mid: number | null } | null>(null);
  const [closeTarget, setCloseTarget] = useState<WheelPosition | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isDiscovery && ticker) {
        // Discovery mode: skip here — loading triggered by detailExpiry effect
        setLoading(false);
        return;
      } else if (id) {
        const s = await wheelService.getStrategy(parseInt(id));
        setStrategy(s);
        // Default mode logic:
        // - selling_calls state → always show recommended (user needs to pick a call to sell)
        // - selling_puts with open option trades → hide (position already entered)
        // - selling_puts with no open trades → recommended (user needs to pick a put)
        const openOptionTrades = (s.trades || []).filter(
          (t: any) => t.status === 'open' && t.strategy_name !== 'assigned_shares'
        ).length;
        if (!opModeInitializedRef.current) {
          const initial: OpMode = s.state === 'selling_calls'
            ? 'recommended'
            : openOptionTrades > 0 ? 'hide' : 'recommended';
          setOpMode(initial);
          opModeRef.current = initial;
          opModeInitializedRef.current = true;
        } else if (s.state !== 'selling_calls' && openOptionTrades > 0 && opModeRef.current !== 'hide') {
          setOpMode('hide');
          opModeRef.current = 'hide';
        } else if (openOptionTrades === 0 && opModeRef.current === 'hide') {
          setOpMode('recommended');
          opModeRef.current = 'recommended';
        }
        // Chain loading is handled by the detailExpiry effect
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, ticker, isDiscovery]);

  useEffect(() => { load(); }, [load]);

  // Fetch available expiries for the detail page dropdown
  const effectiveTicker = ticker || strategy?.ticker;
  useEffect(() => {
    if (!effectiveTicker) return;
    wheelService.getTickerExpiries(effectiveTicker).then((exps) => {
      setAvailableExpiries(exps.map((e) => ({ date: e.date, label: e.label })));
      if (!detailExpiry && exps.length > 0) {
        const stored = localStorage.getItem('wheel_preferred_expiry');
        const today = new Date().toISOString().slice(0, 10);
        if (stored && stored >= today && exps.some((e) => e.date === stored)) {
          setDetailExpiry(stored);
        } else {
          const target = exps[Math.min(5, exps.length - 1)];
          setDetailExpiry(target.date);
        }
      }
    }).catch(() => {});
  }, [effectiveTicker]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load chain when detail expiry is set or changes
  useEffect(() => {
    if (!effectiveTicker || !detailExpiry) return;
    const ot: 'P' | 'C' = strategy?.state === 'selling_calls' ? 'C' : 'P';
    const mode = opModeRef.current === 'hide' ? 'recommended' : opModeRef.current;
    const assignedStrike = strategy?.state === 'selling_calls' && strategy?.assigned_strike
      ? parseFloat(strategy.assigned_strike) : undefined;
    setDiscoveryLoading(true);
    wheelService.getDiscoveryDetail(effectiveTicker, mode, ot, true, detailExpiry, assignedStrike)
      .then((d) => { setDiscovery(d); setDiscoveryLoading(false); })
      .catch((e) => { setError(e.message); setDiscoveryLoading(false); });
    wheelService.refreshChain(effectiveTicker, mode, ot, detailExpiry, assignedStrike)
      .then((d) => setDiscovery(d))
      .catch(() => {});
  }, [detailExpiry, effectiveTicker, strategy?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateWheel = async () => {
    if (!ticker) return;
    const s = await wheelService.createStrategy({ ticker });
    navigate(`/portfolio/wheel/${s.id}`, { state: { expiry: detailExpiry } });
  };

  const handleLogPosition = async (data: { premiumPerShare: number; openDate: string; notes: string }) => {
    if (!strategy || !selectedOption) return;
    const optType = strategy.state === 'selling_puts' ? 'P' : 'C';
    await wheelService.addPosition(strategy.id, {
      option_type: optType,
      strike: selectedOption.strike,
      expiry: selectedOption.expiry,
      premium_per_share: data.premiumPerShare,
      open_date: data.openDate,
      notes: data.notes,
    });
    load();
  };

  const handleClosePosition = async (data: { close_reason: any; close_date: string; close_price?: number }) => {
    if (!closeTarget) return;
    await wheelService.closePosition(strategy!.id, closeTarget.id, data);
    load();
  };

  const handleDeleteWheel = async () => {
    if (!strategy) return;
    await wheelService.deleteStrategy(strategy.id);
    navigate('/portfolio/wheel');
  };

  const handleCloseWheel = async () => {
    if (!strategy) return;
    await wheelService.updateState(strategy.id, 'closed');
    navigate('/portfolio/wheel');
  };

  const handleOpModeChange = async (_: unknown, newMode: OpMode | null) => {
    if (!newMode || newMode === opMode) return;
    setOpMode(newMode);
    opModeRef.current = newMode;
    const t = strategy?.ticker || ticker;
    if (newMode !== 'hide' && t) {
      const ot: 'P' | 'C' = strategy?.state === 'selling_calls' ? 'C' : 'P';
      const assignedStrike = strategy?.state === 'selling_calls' && strategy?.assigned_strike
        ? parseFloat(strategy.assigned_strike) : undefined;
      const exp = detailExpiry || undefined;
      setDiscoveryLoading(true);
      try {
        const dbDisc = await wheelService.getDiscoveryDetail(t, newMode, ot, true, exp, assignedStrike);
        setDiscovery(dbDisc);
        setDiscoveryLoading(false);
        wheelService.refreshChain(t, newMode, ot, exp, assignedStrike)
          .then((liveDisc) => setDiscovery(liveDisc))
          .catch(() => {});
      } catch {
        setDiscoveryLoading(false);
      }
    }
  };

  // Live price polling during market hours
  const pollTicker = useMemo(() => ticker ?? strategy?.ticker ?? '', [ticker, strategy?.ticker]);
  useMarketPolling(
    pollTicker ? [pollTicker] : [],
    useCallback((prices: Record<string, number>) => {
      const live = prices[pollTicker];
      if (live != null) {
        setDiscovery((prev) =>
          prev ? { ...prev, underlying_price: String(live) } : prev,
        );
      }
    }, [pollTicker]),
  );

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  const displayTicker = ticker ?? strategy?.ticker ?? '';

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Top toolbar: back + delete/close wheel */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Back</Button>
        {strategy && (
          <>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              color="success"
              disabled={strategy.open_trade_count > 0}
              onClick={() => setCloseWheelConfirmOpen(true)}
              sx={{ fontWeight: 700 }}
            >
              Close Wheel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => setDeleteConfirmOpen(true)}
              sx={{ fontWeight: 700 }}
            >
              Delete Wheel
            </Button>
          </>
        )}
      </Box>

      {/* Header row: ticker info | APR stats */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        {/* Left: ticker info */}
        <Box sx={{ minWidth: 160 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
            <Typography variant="h4" fontWeight={700}>{displayTicker}</Typography>
            {discovery && (
              <Typography variant="h6" color="text.secondary" fontWeight={500}>
                {fmt.currency(discovery.underlying_price)}
              </Typography>
            )}
          </Box>
          {discovery?.max_pain && (
            <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
              Max Pain: {fmt.currency(discovery.max_pain)}
            </Typography>
          )}
          {strategy && (
            <Chip
              label={strategy.state === 'selling_puts' ? 'Selling PUTs' : 'Selling CALLs'}
              color={strategy.state === 'selling_puts' ? 'primary' : 'secondary'}
              size="small"
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>

        {/* Right: APR stats or create button */}
        {strategy && (() => {
          const pnl = parseFloat(strategy.total_pnl || '0');

          // Annualized ROI: based on realized P&L, collateral = assigned_strike * 100 (or first trade strike)
          const trades = strategy.trades || [];
          const firstTrade = trades.length > 0
            ? trades.reduce((earliest: any, t: any) => (!earliest || t.entry_date < earliest.entry_date) ? t : earliest, null)
            : null;
          const startDate = firstTrade ? new Date(firstTrade.entry_date) : null;
          const endDate = strategy.state === 'closed' && strategy.updated_at
            ? new Date(strategy.updated_at)
            : new Date();
          const daysHeld = startDate ? Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / 86400000)) : null;
          // Collateral = assigned strike × 100 shares, or first put strike × 100
          const collateralStrike = strategy.assigned_strike
            ? parseFloat(strategy.assigned_strike)
            : (firstTrade?.legs?.[0]?.strike || 0);
          const collateral = collateralStrike * 100;
          const annRoi = daysHeld && collateral > 0
            ? (pnl / collateral) * (365 / daysHeld) * 100
            : null;

          return (
          <Grid container spacing={2} sx={{ maxWidth: 480 }}>
            {[
              { label: 'Open Trades', value: String(strategy.open_trade_count || 0) },
              { label: 'Total Trades', value: String(strategy.total_trade_count || 0) },
              { label: 'Total P&L', value: `${pnl >= 0 ? '+' : ''}$${pnl.toFixed(0)}`, color: pnl >= 0 ? 'success.main' : 'error.main' },
              { label: 'Ann. ROI', value: annRoi != null ? `${annRoi >= 0 ? '+' : ''}${annRoi.toFixed(1)}%` : '-', color: annRoi != null && annRoi >= 0 ? 'success.main' : annRoi != null ? 'error.main' : undefined },
            ].map(({ label, value, color }) => (
              <Grid item xs={3} key={label}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography fontWeight={700} color={color}>{value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          );
        })()}
        {isDiscovery && (
          <Button variant="contained" onClick={handleCreateWheel}>Create Wheel</Button>
        )}
      </Box>

      {/* Wheel positions — shown above the toggle */}
      {strategy?.trades && strategy.trades.length > 0 && (
        <PositionsList
          trades={strategy.trades}
          variant="table"
          closeReasons={WHEEL_REASONS}
          onClose={async (tradeId, data) => {
            await wheelService.closePosition(strategy!.id, tradeId, {
              close_reason: data.close_reason,
              close_date: data.close_date,
              close_price: data.close_price,
            });
            load();
          }}
          onDelete={async (tradeId) => {
            await userService.deleteTrade(tradeId);
            load();
          }}
        />
      )}

      {/* Opportunities mode toggle — full-width prominent row */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
        <ToggleButtonGroup
          value={opMode}
          exclusive
          onChange={handleOpModeChange}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            '& .MuiToggleButton-root': {
              flex: { xs: 1, sm: 'none' },
              px: { xs: 1, sm: 3 },
              py: 1,
              fontSize: '0.9rem',
              fontWeight: 600,
              textTransform: 'none',
            },
            boxShadow: 2,
            borderRadius: 1,
          }}
        >
          <ToggleButton value="recommended">
            Recommended
            {discoveryLoading && opMode === 'recommended' && <CircularProgress size={14} sx={{ ml: 1 }} />}
          </ToggleButton>
          <ToggleButton value="all">
            Active Strikes
            {discoveryLoading && opMode === 'all' && <CircularProgress size={14} sx={{ ml: 1 }} />}
          </ToggleButton>
          {strategy && <ToggleButton value="hide">Hide</ToggleButton>}
        </ToggleButtonGroup>
      </Box>

      {/* Option chain table */}
      {discovery && (isDiscovery || opMode !== 'hide') && (() => {
        const isCalls = strategy?.state === 'selling_calls';
        const typeLabel = isCalls ? 'CALL' : 'PUT';
        const modeLabel = opMode === 'all' ? 'Active Strikes (volume > 0)' : 'Recommended (15–30 delta)';
        return (
        <Box mb={3} sx={{ position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {typeLabel} Options — {modeLabel}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {!customDateMode ? (
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <Select
                    value={availableExpiries.some((e) => e.date === detailExpiry) ? detailExpiry : '__custom_selected__'}
                    onChange={(e) => {
                      if (e.target.value === '__custom__') {
                        setCustomDateMode(true);
                      } else if (e.target.value !== '__custom_selected__') {
                        setDetailExpiry(e.target.value);
                      }
                    }}
                    renderValue={(val) => {
                      const found = availableExpiries.find((e) => e.date === val);
                      if (found) return found.label;
                      if (detailExpiry) {
                        const d = new Date(detailExpiry + 'T12:00:00');
                        const dte = Math.max(1, Math.round((d.getTime() - Date.now()) / 86400000));
                        return `${d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} — ${dte} DTE (custom)`;
                      }
                      return 'Select expiry';
                    }}
                  >
                    {availableExpiries.map((exp) => (
                      <MenuItem key={exp.date} value={exp.date}>{exp.label}</MenuItem>
                    ))}
                    {detailExpiry && !availableExpiries.some((e) => e.date === detailExpiry) && (
                      <MenuItem value="__custom_selected__" sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                        {(() => {
                          const d = new Date(detailExpiry + 'T12:00:00');
                          const dte = Math.max(1, Math.round((d.getTime() - Date.now()) / 86400000));
                          return `${d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} — ${dte} DTE (custom)`;
                        })()}
                      </MenuItem>
                    )}
                    <MenuItem
                      value="__custom__"
                      sx={{
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        fontWeight: 700,
                        bgcolor: 'action.hover',
                        color: 'text.primary',
                        py: 1.5,
                        '&:hover': { bgcolor: 'action.selected' },
                      }}
                    >
                      📅 Pick a date...
                    </MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    type="date"
                    size="small"
                    label="Select expiry date"
                    inputRef={(input) => { if (input) setTimeout(() => input.showPicker?.(), 100); }}
                    defaultValue=""
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: new Date().toISOString().slice(0, 10) }}
                    sx={{ minWidth: 200 }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const val = (e.target as HTMLInputElement).value;
                        if (val) { setDetailExpiry(val); setCustomDateMode(false); }
                      }
                    }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      const input = document.querySelector<HTMLInputElement>('input[type="date"]');
                      const val = input?.value;
                      if (val) { setDetailExpiry(val); setCustomDateMode(false); }
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Go
                  </Button>
                  <Button size="small" onClick={() => setCustomDateMode(false)} sx={{ textTransform: 'none' }}>
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
          {discoveryLoading && (
            <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
              <CircularProgress size={16} />
            </Box>
          )}
          <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ opacity: discoveryLoading ? 0.6 : 1, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                  {([
                    { id: 'expiry',         label: 'Expiry',   align: 'left',  hide: false },
                    { id: 'strike',         label: 'Strike',   align: 'right', hide: false },
                    { id: 'delta',          label: 'Delta',    align: 'right', hide: false },
                    { id: 'bid',            label: 'Bid',      align: 'right', hide: true  },
                    { id: 'ask',            label: 'Ask',      align: 'right', hide: true  },
                    { id: 'mid',            label: 'Mid',      align: 'right', hide: false },
                    { id: 'iv',             label: 'IV',       align: 'right', hide: true  },
                    { id: 'dte',            label: 'DTE',      align: 'right', hide: true  },
                    { id: 'volume',         label: 'Volume',   align: 'right', hide: true  },
                    { id: 'open_interest',  label: 'OI',       align: 'right', hide: true  },
                    { id: 'annualized_roi', label: 'Ann. ROI', align: 'right', hide: false },
                  ] as { id: SortCol; label: string; align: 'left' | 'right'; hide: boolean }[]).map(col => (
                    <TableCell key={col.id} align={col.align} sx={col.hide ? { display: { xs: 'none', sm: 'table-cell' } } : undefined}>
                      <TableSortLabel
                        active={sortCol === col.id}
                        direction={sortCol === col.id ? sortDir : 'asc'}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  {!isDiscovery && <TableCell align="right">Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPuts(discovery.puts).map((row, i) => {
                  const isAssignedStrike = strategy?.state === 'selling_calls'
                    && strategy?.assigned_strike
                    && Number(row.strike) === Number(strategy.assigned_strike);
                  return (
                  <TableRow key={i} hover sx={isAssignedStrike ? { bgcolor: 'action.selected', borderLeft: '3px solid', borderColor: 'primary.main' } : undefined}>
                    <TableCell>{fmt.shortDate(row.expiry)}</TableCell>
                    <TableCell align="right">{fmt.strike(row.strike)}</TableCell>
                    <TableCell align="right">{fmt.delta(row.delta)}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{fmt.currency(row.bid)}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{fmt.currency(row.ask)}</TableCell>
                    <TableCell align="right"><strong>{fmt.currency(row.mid)}</strong></TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{fmt.iv(row.iv)}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{row.dte ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{row.volume?.toLocaleString() ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{row.open_interest?.toLocaleString() ?? '—'}</TableCell>
                    <TableCell align="right">
                      {row.annualized_roi
                        ? <Typography color="success.main" fontWeight={600}>{fmt.percent(row.annualized_roi)}</Typography>
                        : '—'
                      }
                    </TableCell>
                    {!isDiscovery && (
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setSelectedOption({
                              strike: Number(row.strike),
                              expiry: row.expiry,
                              mid: row.mid ? Number(row.mid) : null,
                            });
                            setExecuteOpen(true);
                          }}
                        >
                          Enter
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>);
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {opMode === 'recommended' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Showing strikes with delta between 0.15 and 0.30 (closest to 0.20 target).
              Only strikes from the latest snapshot with valid delta and bid/ask are included.
              Switch to "All Strikes" to see every strike with volume &gt; 0.
            </Typography>
          )}
        </Box>
        );
      })()}

      {/* Positions table — removed, shown in summary strip above */}

      {/* Popups */}
      {executeOpen && selectedOption && strategy && (
        <ExecuteLogPopup
          open={executeOpen}
          tradeDetails={{
            ticker: strategy.ticker,
            action: strategy.state === 'selling_puts' ? 'Sell PUT' : 'Sell CALL',
            strike: selectedOption.strike,
            expiry: selectedOption.expiry,
            estimatedCredit: selectedOption.mid ?? undefined,
          } as TradeDetails}
          onClose={() => setExecuteOpen(false)}
          onLog={handleLogPosition}
        />
      )}

      {closeTarget && (
        <ClosePositionPopup
          open={!!closeTarget}
          ticker={strategy?.ticker ?? ''}
          strike={closeTarget.strike}
          expiry={closeTarget.expiry}
          onClose={() => setCloseTarget(null)}
          onConfirm={handleClosePosition}
        />
      )}

      {/* Delete Wheel confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Wheel?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the {strategy?.ticker} wheel and all its positions. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteWheel}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Close Wheel confirmation */}
      <Dialog open={closeWheelConfirmOpen} onClose={() => setCloseWheelConfirmOpen(false)}>
        <DialogTitle>Close Wheel?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Mark the {strategy?.ticker} wheel as closed. It will no longer appear in your active wheels list.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseWheelConfirmOpen(false)}>Cancel</Button>
          <Button color="success" variant="contained" onClick={handleCloseWheel}>Close Wheel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WheelDetail;
